/**
 * SynapseSkillEffect — Conjuration Tether path upgrade: twin attack stagger.
 *
 * When the player and shade hit the same enemy within a short window
 * (twinAttackWindowMs, default 400ms), the target staggers for
 * twinAttackStaggerMs (default 500ms). The stagger participates in the
 * CC hierarchy — it won't land on stunned targets or during a stagger
 * immunity window.
 *
 * The core mechanic lives in the embedded TwinAttackTracker: a hit log
 * keyed by enemy ID and source (player vs shade). Each hit records its
 * timestamp. When the *other* source strikes the same enemy within the
 * window, a twin attack fires and the log entries for that enemy are
 * consumed. Entries expire passively after twinAttackWindowMs to prevent
 * ghost matches across distant hits.
 *
 * Integration hooks:
 *   - recordPlayerHit(targetId, timestampMs) — called by CombatHitResolver
 *     after a player attack connects.
 *   - recordShadeHit(targetId, timestampMs) — called by CombatHitResolver
 *     (or shade attack handler) after a shade attack connects.
 *   - Both return { twinAttack: true, staggerMs } when a twin attack fires,
 *     or { twinAttack: false } when it doesn't. The caller applies stagger
 *     through StatusEffectTracker.apply('stagger', ...).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see conjuration-spec.md § Synapse (tether_synapse)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SynapseConfig {
  /** Duration of twin-attack stagger in ms. */
  readonly twinAttackStaggerMs: number;
  /** Window in ms within which both parties must hit to trigger. */
  readonly twinAttackWindowMs: number;
}

const DEFAULT_CONFIG: SynapseConfig = {
  twinAttackStaggerMs: 500,
  twinAttackWindowMs: 400,
};

export interface TwinAttackResult {
  readonly twinAttack: boolean;
  /** Present when twinAttack is true. */
  readonly staggerMs?: number;
}

// ---------------------------------------------------------------------------
// Twin Attack Tracker
// ---------------------------------------------------------------------------

/**
 * Tracks recent hits from any source (player, individual shades) against
 * individual enemies, detecting when two *different* sources strike the
 * same target within the configured window.
 *
 * Each source maintains its own hit log keyed by target ID. When a new
 * hit arrives, all *other* source logs are checked for a matching target
 * within the window. On match, the twin attack fires and the matched
 * entry is consumed.
 */
export class TwinAttackTracker {
  /**
   * Per-source hit logs. Outer key = sourceId ('player', shade IDs, etc.),
   * inner key = targetId, value = timestamp of most recent hit.
   */
  private readonly sourceHits = new Map<string, Map<string, number>>();

  /** Running count of twin attacks fired (observable for testing). */
  private _twinAttackCount = 0;

  constructor(private readonly config: SynapseConfig) {}

  get twinAttackCount(): number { return this._twinAttackCount; }

  /**
   * Record a player hit. Checks whether any other source hit the same
   * enemy within the twin-attack window.
   */
  recordPlayerHit(targetId: string, timestampMs: number): TwinAttackResult {
    return this.recordHit('player', targetId, timestampMs);
  }

  /**
   * Record a shade hit. Checks whether any other source (player or a
   * different shade) hit the same enemy within the twin-attack window.
   *
   * @param shadeId — unique identifier for the shade source. Defaults
   *   to 'shade' for backward compatibility with single-shade setups.
   */
  recordShadeHit(targetId: string, timestampMs: number, shadeId: string = 'shade'): TwinAttackResult {
    return this.recordHit(shadeId, targetId, timestampMs);
  }

  /** Clear all tracked hits (e.g., on skill deactivation or shade despawn). */
  clear(): void {
    this.sourceHits.clear();
  }

  /**
   * Core hit recording logic. Records a hit from `sourceId` against
   * `targetId`, then scans every *other* source for a matching hit
   * within the twin-attack window.
   */
  private recordHit(sourceId: string, targetId: string, timestampMs: number): TwinAttackResult {
    // Ensure this source has a hit log
    let ownLog = this.sourceHits.get(sourceId);
    if (!ownLog) {
      ownLog = new Map<string, number>();
      this.sourceHits.set(sourceId, ownLog);
    }

    // Expire any stale entry for this source + target before recording
    this.expireEntry(ownLog, targetId, timestampMs);

    // Check every other source for a matching hit within the window
    for (const [otherSourceId, otherLog] of this.sourceHits) {
      if (otherSourceId === sourceId) continue;

      const otherTs = otherLog.get(targetId);
      if (otherTs !== undefined && (timestampMs - otherTs) <= this.config.twinAttackWindowMs) {
        // Twin attack! Consume the matched entry.
        otherLog.delete(targetId);
        // Don't record the current hit — it's been consumed by the twin attack
        this._twinAttackCount++;
        return { twinAttack: true, staggerMs: this.config.twinAttackStaggerMs };
      }
    }

    // No match — record the hit for future cross-source checks
    ownLog.set(targetId, timestampMs);
    return { twinAttack: false };
  }

  /**
   * Expire a single entry if it's older than the window. Called before
   * recording a new hit to prevent ghost matches with stale entries.
   */
  private expireEntry(log: Map<string, number>, targetId: string, nowMs: number): void {
    const ts = log.get(targetId);
    if (ts !== undefined && (nowMs - ts) > this.config.twinAttackWindowMs) {
      log.delete(targetId);
    }
  }
}

// ---------------------------------------------------------------------------
// Skill Effect
// ---------------------------------------------------------------------------

export class SynapseSkillEffect implements SkillEffect {
  readonly skillId = 'tether_synapse';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  readonly tracker: TwinAttackTracker;

  constructor(config: Partial<SynapseConfig> = {}) {
    const resolved = { ...DEFAULT_CONFIG, ...config };
    this.tracker = new TwinAttackTracker(resolved);
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // upgrade — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Upgrade — twin attack detection is event-driven, not tick-driven
  }

  destroy(): void {
    this.tracker.clear();
  }
}
